import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Users, Crown, Shield, CreditCard, BarChart3,
  Loader2, Search, ChevronDown, ChevronUp, ExternalLink,
  IndianRupee, Calendar, TrendingUp, Activity, Bell,
  CheckCircle, XCircle, Eye, Clock, Image, Globe, Plus, Trash2, Sparkles,
  Newspaper, Settings, RefreshCw, MapPin, MessageSquare, Mail, Tag, EyeOff, Lock, KeyRound, AlertTriangle, Wrench, UserCog, DatabaseZap, Gauge, Gift, Ticket, GitBranch
} from 'lucide-react';
import FounderCockpitTab from './admin/FounderCockpitTab';
import ChangeLogTab from './admin/ChangeLogTab';
import SurgicalCouponsTab from './admin/SurgicalCouponsTab';
import SurgicalCatalogsTab from './admin/SurgicalCatalogsTab';

const ADMIN_TABS = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'cockpit', label: 'Founder Cockpit', icon: Gauge },
  { key: 'payments', label: 'Payment Requests', icon: Bell },
  { key: 'doctors', label: 'Doctors', icon: Users },
  { key: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
  { key: 'seo', label: 'SEO Command Center', icon: Newspaper },
  { key: 'feedback', label: 'Feedback', icon: MessageSquare },
  { key: 'coupons', label: 'Coupons', icon: Tag },
  { key: 'surgical-coupons', label: 'Surgical Suite', icon: Ticket },
  { key: 'surgical-catalogs', label: 'Surgical Catalogs', icon: Ticket },
  { key: 'country-admins', label: 'Country Admins', icon: Globe },
  { key: 'security', label: 'Security', icon: Lock },
  { key: 'data-fixes', label: 'Data Fixes', icon: Wrench },
  { key: 'changelog', label: 'Change Log', icon: GitBranch },
];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [pendingCount, setPendingCount] = useState(0);
  const [feedbackNewCount, setFeedbackNewCount] = useState(0);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin' && user.role !== 'country_admin') { navigate('/'); return; }
    api.get('/admin/stats').then(r => setPendingCount(r.data.pending_payments || 0)).catch(() => {});
    api.get('/admin/feedback/unread-count').then(r => setFeedbackNewCount(r.data.count || 0)).catch(() => {});
  }, [user, loading, navigate]);

  if (loading || !user || (user.role !== 'admin' && user.role !== 'country_admin')) return null;

  const isSuperAdmin = user.role === 'admin';
  // Founder Cockpit is super-admin-only (founder analytics)
  const visibleTabs = isSuperAdmin
    ? ADMIN_TABS
    : ADMIN_TABS.filter(t => t.key !== 'country-admins' && t.key !== 'cockpit' && t.key !== 'coupons' && t.key !== 'surgical-coupons' && t.key !== 'surgical-catalogs' && t.key !== 'changelog');

  return (
    <div className="min-h-screen bg-slate-50/50" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="admin-title">
            Platform Admin
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            DocSite Management Console
            {!isSuperAdmin && user.country && <span className="ml-2 px-2 py-0.5 rounded bg-sky-100 text-sky-700 text-xs font-medium">{user.country}</span>}
          </p>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto pb-1" data-testid="admin-tabs">
          {visibleTabs.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${activeTab === key ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
              data-testid={`admin-tab-${key}`}>
              <Icon size={14} /> {label}
              {key === 'payments' && pendingCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="pending-badge">
                  {pendingCount}
                </span>
              )}
              {key === 'feedback' && feedbackNewCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="feedback-new-badge">
                  {feedbackNewCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && <OverviewTab pendingCount={pendingCount} onViewPayments={() => setActiveTab('payments')} />}
        {activeTab === 'cockpit' && isSuperAdmin && <FounderCockpitTab />}
        {activeTab === 'payments' && <PaymentRequestsTab onCountChange={setPendingCount} />}
        {activeTab === 'doctors' && <DoctorsTab />}
        {activeTab === 'subscriptions' && <SubscriptionsTab />}
        {activeTab === 'seo' && <SeoCommandCenterTab />}
        {activeTab === 'feedback' && <FeedbackTab onCountChange={setFeedbackNewCount} />}
        {activeTab === 'coupons' && isSuperAdmin && <CouponsTab />}
        {activeTab === 'surgical-coupons' && isSuperAdmin && <SurgicalCouponsTab />}
        {activeTab === 'surgical-catalogs' && isSuperAdmin && <SurgicalCatalogsTab />}
        {activeTab === 'country-admins' && isSuperAdmin && <CountryAdminsTab />}
        {activeTab === 'security' && isSuperAdmin && <SecurityTab />}
        {activeTab === 'data-fixes' && isSuperAdmin && <DataFixesTab />}
        {activeTab === 'changelog' && isSuperAdmin && <ChangeLogTab />}
      </div>
    </div>
  );
}

function OverviewTab({ pendingCount, onViewPayments }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin text-slate-600 mx-auto" /></div>;
  if (!stats) return <div className="text-center py-10 text-sm text-slate-500">Failed to load stats</div>;

  const cards = [
    { label: 'Total Doctors', value: stats.total_doctors, icon: Users, color: 'bg-sky-50 text-sky-700 border-sky-200' },
    { label: 'Premium Doctors', value: stats.premium_doctors, icon: Crown, color: 'bg-amber-50 text-amber-700 border-amber-200' },
    { label: 'Free Doctors', value: stats.free_doctors, icon: Shield, color: 'bg-slate-50 text-slate-600 border-slate-200' },
    { label: 'Total Patients', value: stats.total_patients, icon: Activity, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { label: 'Total Appointments', value: stats.total_bookings, icon: Calendar, color: 'bg-violet-50 text-violet-700 border-violet-200' },
    { label: 'Active Subscriptions', value: stats.active_subscriptions, icon: TrendingUp, color: 'bg-rose-50 text-rose-700 border-rose-200' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      {/* Pending payments alert */}
      {pendingCount > 0 && (
        <button onClick={onViewPayments}
          className="w-full bg-red-50 rounded-2xl border border-red-200 p-4 flex items-center gap-3 hover:bg-red-100 transition-colors text-left"
          data-testid="pending-alert">
          <div className="w-10 h-10 rounded-xl bg-red-500 flex items-center justify-center flex-shrink-0">
            <Bell size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-900">{pendingCount} Payment{pendingCount > 1 ? 's' : ''} Awaiting Verification</p>
            <p className="text-xs text-red-600">Click to review and approve/reject pending UPI payments</p>
          </div>
          <ChevronDown size={16} className="text-red-400 rotate-[-90deg]" />
        </button>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className={`rounded-2xl border p-5 ${color}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between mb-2"><Icon size={18} /></div>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
            <p className="text-xs font-medium mt-1 opacity-80">{label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="revenue-card">
        <div className="flex items-center gap-3 mb-2">
          <IndianRupee size={20} className="text-emerald-600" />
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Revenue</p>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. {stats.total_revenue_display}</p>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-1">From approved subscriptions (UPI + Razorpay)</p>
      </div>

      <PreviewFunnelCard />
      <DoctorSignupSourcesCard />

      <BulkPincodeUpdater />
    </div>
  );
}

// Preview funnel — shows /preview/my-clinics traffic + sticky banner conversion
function PreviewFunnelCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('week');

  useEffect(() => {
    api.get('/admin/analytics/preview-summary')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="preview-funnel-loading">
        <Loader2 className="animate-spin text-slate-400 mx-auto" size={18} />
      </div>
    );
  }
  if (!data) return null;

  const w = data[tab] || {};
  const views = w.views?.count || 0;
  const uniques = w.views?.uniques || 0;
  const stickyShown = w.sticky_shown?.count || 0;
  const stickyCta = w.sticky_cta?.count || 0;
  const bottomCta = w.bottom_cta?.count || 0;
  const dismissed = w.dismissed?.count || 0;
  const ctr = w.ctr_percent || 0;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="preview-funnel-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BarChart3 size={20} className="text-sky-600" />
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Patient Preview Funnel</p>
            <p className="text-sm text-slate-700">Traffic + sign-up conversion on <code className="text-xs bg-slate-50 px-1.5 py-0.5 rounded">/preview/my-clinics</code></p>
          </div>
        </div>
        <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5 text-xs" data-testid="preview-funnel-tabs">
          {['today', 'week', 'month'].map(k => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-3 py-1.5 rounded-full font-semibold transition-colors ${tab === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`preview-funnel-tab-${k}`}
            >
              {k === 'today' ? '24h' : k === 'week' ? '7 days' : '30 days'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl bg-sky-50 border border-sky-100 p-4">
          <p className="text-2xl font-bold text-sky-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{views}</p>
          <p className="text-[11px] text-sky-700 mt-1">Page views</p>
          <p className="text-[10px] text-slate-500 mt-0.5">{uniques} uniques</p>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
          <p className="text-2xl font-bold text-emerald-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{stickyShown}</p>
          <p className="text-[11px] text-emerald-700 mt-1">Sticky shown</p>
          <p className="text-[10px] text-slate-500 mt-0.5">{dismissed} dismissed</p>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
          <p className="text-2xl font-bold text-amber-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{stickyCta + bottomCta}</p>
          <p className="text-[11px] text-amber-700 mt-1">Sign-up clicks</p>
          <p className="text-[10px] text-slate-500 mt-0.5">{stickyCta} sticky · {bottomCta} bottom</p>
        </div>
        <div className="rounded-xl bg-violet-50 border border-violet-100 p-4">
          <p className="text-2xl font-bold text-violet-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{ctr}%</p>
          <p className="text-[11px] text-violet-700 mt-1">View → click rate</p>
          <p className="text-[10px] text-slate-500 mt-0.5">All CTAs combined</p>
        </div>
      </div>

      <p className="text-[10px] text-slate-400 mt-4 italic">Privacy-first: no PII, no cookies, no third-party trackers. UA hashed only.</p>
    </div>
  );
}

// Doctor signup attribution — which CTA / surface is converting best
function DoctorSignupSourcesCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('week');

  useEffect(() => {
    api.get('/admin/analytics/signup-sources')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="signup-sources-loading">
        <Loader2 className="animate-spin text-slate-400 mx-auto" size={18} />
      </div>
    );
  }
  if (!data) return null;

  const w = data[tab] || { total: 0, rows: [] };
  const total = w.total || 0;
  const max = w.rows.length ? Math.max(...w.rows.map(r => r.count)) : 1;

  // Friendly labels for the source slugs we ship in the URLs
  const FRIENDLY = {
    header: 'Header — "Get Started in 3 Clicks"',
    header_mobile: 'Header (mobile menu)',
    footer: 'Footer link',
    landing_hero: 'Landing page hero',
    landing_bottom: 'Landing page bottom CTA',
    brochure: 'Product brochure (/brochure)',
    biz_case: 'Business Case page',
    specialty: 'Specialty hub (/for/...)',
    compare: 'Compare page (vs aggregators)',
    resource: 'Resource article',
    '(unknown)': 'Other / direct',
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="signup-sources-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Crown size={20} className="text-amber-600" />
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Doctor Signup Sources</p>
            <p className="text-sm text-slate-700">Which CTA brings doctors in? <span className="text-slate-500">{total} signups in window</span></p>
          </div>
        </div>
        <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5 text-xs" data-testid="signup-sources-tabs">
          {['week', 'month', 'all'].map(k => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-3 py-1.5 rounded-full font-semibold transition-colors ${tab === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`signup-sources-tab-${k}`}
            >
              {k === 'week' ? '7 days' : k === 'month' ? '30 days' : 'All time'}
            </button>
          ))}
        </div>
      </div>

      {w.rows.length === 0 ? (
        <p className="text-sm text-slate-400 italic text-center py-6">No doctor signups in this window yet.</p>
      ) : (
        <div className="space-y-2.5">
          {w.rows.map((r) => {
            const label = FRIENDLY[r.source] || r.source;
            const pct = total ? Math.round((r.count / total) * 100) : 0;
            const barW = Math.max(4, Math.round((r.count / max) * 100));
            return (
              <div key={r.source} className="flex items-center gap-3" data-testid={`signup-source-row-${r.source}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-sm text-slate-700 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{label}</p>
                    <p className="text-sm font-bold text-slate-900 flex-shrink-0">{r.count} <span className="text-xs font-normal text-slate-400">· {pct}%</span></p>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full" style={{ width: `${barW}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-4 italic">Source captured from <code className="bg-slate-50 px-1 rounded text-[10px]">?from=...</code> on each register CTA.</p>
    </div>
  );
}

// ========== BULK AI PINCODE UPDATER ==========
function BulkPincodeUpdater() {
  const [missing, setMissing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/admin/locations-missing-pincode').then(r => setMissing(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleBulkUpdate = async () => {
    if (!window.confirm(`Run AI on ${missing.count} locations to auto-detect pincodes? This uses GPT-4o credits.`)) return;
    setUpdating(true); setResult(null);
    try {
      const { data } = await api.post('/admin/bulk-update-pincodes');
      setResult(data);
      // Refresh missing count
      api.get('/admin/locations-missing-pincode').then(r => setMissing(r.data));
    } catch (e) {
      setResult({ error: formatApiError(e.response?.data?.detail) });
    } finally { setUpdating(false); }
  };

  if (loading) return null;
  if (!missing || missing.count === 0) {
    if (result) {
      return (
        <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-5" data-testid="bulk-pincode-done">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={18} className="text-emerald-600" />
            <p className="text-sm font-medium text-emerald-800">All locations have pincodes</p>
          </div>
          {result.updated > 0 && <p className="text-xs text-emerald-600">AI updated {result.updated} location{result.updated > 1 ? 's' : ''}.</p>}
        </div>
      );
    }
    return null;
  }

  return (
    <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5 space-y-4" data-testid="bulk-pincode-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-amber-900">{missing.count} Location{missing.count > 1 ? 's' : ''} Missing Pincode</p>
          <p className="text-xs text-amber-600 mt-0.5">AI can auto-detect pincodes from addresses. One-click bulk update.</p>
        </div>
        <button onClick={handleBulkUpdate} disabled={updating}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 disabled:opacity-50 whitespace-nowrap"
          data-testid="bulk-update-btn">
          {updating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {updating ? 'AI Updating...' : 'AI Auto-Fix All'}
        </button>
      </div>

      {/* List of locations missing pincode */}
      <div className="space-y-1.5 max-h-40 overflow-y-auto">
        {missing.locations.map(loc => (
          <div key={loc.id} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-2">
            <span className="text-slate-700">{loc.name} <span className="text-slate-400">({loc.doctor_name})</span></span>
            <span className="text-slate-400">{loc.city || loc.address?.slice(0, 30)}</span>
          </div>
        ))}
      </div>

      {/* Result */}
      {result && !result.error && (
        <div className="bg-white rounded-xl p-4 space-y-2" data-testid="bulk-result">
          <p className="text-sm font-medium text-slate-900">
            Updated: <span className="text-emerald-600">{result.updated}</span> | Failed: <span className="text-red-500">{result.failed}</span> / {result.total}
          </p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {result.results?.map(r => (
              <p key={r.id} className={`text-[10px] ${r.status === 'updated' ? 'text-emerald-600' : 'text-red-500'}`}>
                {r.name}: {r.status === 'updated' ? `Updated — ${Object.entries(r.fields || {}).map(([k,v]) => `${k}: ${v}`).join(', ')}` : r.reason}
              </p>
            ))}
          </div>
        </div>
      )}
      {result?.error && <p className="text-xs text-red-600">{result.error}</p>}
    </div>
  );
}

// ========== PAYMENT REQUESTS TAB ==========
function PaymentRequestsTab({ onCountChange }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [expandedImg, setExpandedImg] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const url = filter === 'all' ? '/admin/payment-requests' : `/admin/payment-requests?status=${filter}`;
    api.get(url).then(r => {
      setRequests(r.data);
      // Update pending count
      if (filter === 'pending') onCountChange(r.data.length);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [filter, onCountChange]);
  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this payment and activate Premium for this doctor?')) return;
    setActionLoading(id);
    try {
      await api.post(`/admin/payment-requests/${id}/approve`);
      load();
      // Refresh pending count
      api.get('/admin/payment-requests?status=pending').then(r => onCountChange(r.data.length));
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectId) return;
    setActionLoading(rejectId);
    try {
      await api.post(`/admin/payment-requests/${rejectId}/reject`, { reason: rejectReason });
      setRejectId(null);
      setRejectReason('');
      load();
      api.get('/admin/payment-requests?status=pending').then(r => onCountChange(r.data.length));
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin text-slate-600 mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-payment-requests">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>UPI Payment Verifications</h3>
        <div className="flex gap-1">
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${filter === f ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-100 border border-slate-200'}`}
              data-testid={`filter-${f}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
          <Bell size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600">No {filter !== 'all' ? filter : ''} payment requests</p>
        </div>
      ) : (
        requests.map(req => (
          <div key={req.id} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid={`pay-req-${req.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-900">{req.doctor_name}</p>
                <p className="text-xs text-slate-500">{req.doctor_email} &middot; {req.doctor_phone}</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                req.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                req.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                'bg-red-100 text-red-700'
              }`}>
                {req.status.toUpperCase()}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div><span className="text-slate-500">Plan:</span> <span className="font-medium">{req.plan_name}</span></div>
              <div><span className="text-slate-500">Amount:</span> <span className="font-medium">Rs. {req.display_amount}</span></div>
              <div><span className="text-slate-500">UTR:</span> <span className="font-mono font-medium">{req.utr_number}</span></div>
              <div><span className="text-slate-500">Date:</span> <span className="font-medium">{req.created_at?.split('T')[0]}</span></div>
            </div>

            {/* Screenshot preview */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setExpandedImg(expandedImg === req.id ? null : req.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 text-xs font-medium"
                data-testid={`view-screenshot-${req.id}`}>
                <Image size={12} /> {expandedImg === req.id ? 'Hide' : 'View'} Screenshot
              </button>
              <a href={`${process.env.REACT_APP_BACKEND_URL}${req.screenshot_url}`} target="_blank" rel="noopener noreferrer"
                className="text-xs text-sky-600 hover:text-sky-700 font-medium flex items-center gap-1">
                <ExternalLink size={10} /> Open Full Size
              </a>
            </div>

            {expandedImg === req.id && (
              <div className="rounded-xl overflow-hidden border border-slate-200 max-w-md">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${req.screenshot_url}`}
                  alt="Payment screenshot"
                  className="w-full h-auto"
                  data-testid={`screenshot-img-${req.id}`}
                />
              </div>
            )}

            {req.admin_note && (
              <div className="bg-red-50 rounded-lg p-3 text-xs text-red-700">
                <span className="font-medium">Rejection reason:</span> {req.admin_note}
              </div>
            )}

            {/* Action buttons for pending requests */}
            {req.status === 'pending' && (
              <div className="flex gap-2 pt-2 border-t border-slate-100">
                <button onClick={() => handleApprove(req.id)}
                  disabled={actionLoading === req.id}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
                  data-testid={`approve-${req.id}`}>
                  {actionLoading === req.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                  Approve & Activate Premium
                </button>
                <button onClick={() => setRejectId(req.id)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 border border-red-200"
                  data-testid={`reject-btn-${req.id}`}>
                  <XCircle size={12} /> Reject
                </button>
              </div>
            )}

            {/* Reject reason modal inline */}
            {rejectId === req.id && (
              <div className="bg-red-50 rounded-xl p-4 space-y-3 border border-red-200" data-testid={`reject-form-${req.id}`}>
                <p className="text-xs font-medium text-red-800">Rejection Reason (optional):</p>
                <input type="text"
                  className="w-full rounded-lg border border-red-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-200"
                  value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                  placeholder="e.g., Payment not received, invalid screenshot..."
                  data-testid={`reject-reason-${req.id}`}
                />
                <div className="flex gap-2">
                  <button onClick={handleReject}
                    disabled={actionLoading === req.id}
                    className="px-4 py-1.5 rounded-full bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                    data-testid={`confirm-reject-${req.id}`}>
                    Confirm Reject
                  </button>
                  <button onClick={() => { setRejectId(null); setRejectReason(''); }}
                    className="px-4 py-1.5 rounded-full bg-white text-slate-600 text-xs font-medium border border-slate-200 hover:bg-slate-50">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function DoctorsTab() {
  const [doctors, setDoctors] = useState([]);
  const [geoOptions, setGeoOptions] = useState({ countries: [], states: [], cities: [], pincodes: [], specialties: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({ country: '', state: '', city: '', pincode: '', specialty: '', tier: '' });

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.country) params.append('country', filters.country);
    if (filters.state) params.append('state', filters.state);
    if (filters.city) params.append('city', filters.city);
    if (filters.pincode) params.append('pincode', filters.pincode);
    if (filters.specialty) params.append('specialty', filters.specialty);
    if (filters.tier) params.append('tier', filters.tier);
    const qs = params.toString();
    api.get(`/admin/doctors${qs ? '?' + qs : ''}`).then(r => setDoctors(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/admin/geo-options').then(r => setGeoOptions(r.data)).catch(() => {});
  }, []);

  const handleSetTier = async (doctorId, tier) => {
    try { await api.post(`/admin/doctors/${doctorId}/set-tier`, { tier }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail)); }
  };

  const resetFilters = () => setFilters({ country: '', state: '', city: '', pincode: '', specialty: '', tier: '' });

  const textFiltered = doctors.filter(d =>
    !search || d.name?.toLowerCase().includes(search.toLowerCase()) ||
    d.email?.toLowerCase().includes(search.toLowerCase()) ||
    d.slug?.toLowerCase().includes(search.toLowerCase())
  );

  const hasActiveFilters = Object.values(filters).some(v => v);

  return (
    <div className="space-y-4" data-testid="admin-doctors">
      {/* Geo Filter Bar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="geo-filters">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Filter Doctors</p>
          {hasActiveFilters && (
            <button onClick={resetFilters} className="text-[10px] text-sky-600 hover:text-sky-700 font-medium" data-testid="clear-filters">Clear all filters</button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <select className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs outline-none" value={filters.country} onChange={e => setFilters(f => ({ ...f, country: e.target.value, state: '', city: '' }))} data-testid="filter-country">
            <option value="">All Countries</option>
            {geoOptions.countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs outline-none" value={filters.state} onChange={e => setFilters(f => ({ ...f, state: e.target.value, city: '' }))} data-testid="filter-state">
            <option value="">All States</option>
            {geoOptions.states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs outline-none" value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value }))} data-testid="filter-city">
            <option value="">All Cities</option>
            {geoOptions.cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="text" placeholder="Pincode" className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs outline-none font-mono" value={filters.pincode} onChange={e => setFilters(f => ({ ...f, pincode: e.target.value }))} data-testid="filter-pincode" />
          <select className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs outline-none" value={filters.specialty} onChange={e => setFilters(f => ({ ...f, specialty: e.target.value }))} data-testid="filter-specialty">
            <option value="">All Specialties</option>
            {geoOptions.specialties.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs outline-none" value={filters.tier} onChange={e => setFilters(f => ({ ...f, tier: e.target.value }))} data-testid="filter-tier">
            <option value="">All Tiers</option>
            <option value="free">Free</option>
            <option value="premium">Premium</option>
          </select>
        </div>
      </div>

      {/* Search + Count */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search by name, email, or slug..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
            value={search} onChange={e => setSearch(e.target.value)} data-testid="doctor-search" />
        </div>
        <span className="text-xs text-slate-500 whitespace-nowrap">{textFiltered.length} doctors</span>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="animate-spin text-slate-600 mx-auto" /></div>
      ) : textFiltered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
          <Users size={28} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600">No doctors match the current filters</p>
          {hasActiveFilters && <button onClick={resetFilters} className="mt-2 text-xs text-sky-600 hover:text-sky-700 font-medium">Clear filters</button>}
        </div>
      ) : (
        textFiltered.map(doc => (
          <div key={doc.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`admin-doctor-${doc.slug}`}>
            <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => setExpanded(expanded === doc.id ? null : doc.id)}>
              <div className="flex items-center gap-3">
                {doc.photo_url ? (
                  <img src={doc.photo_url.startsWith('http') ? doc.photo_url : `${process.env.REACT_APP_BACKEND_URL}${doc.photo_url}`} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400"><Users size={16} /></div>
                )}
                <div>
                  <p className="text-sm font-medium text-slate-900">{doc.name}</p>
                  <p className="text-xs text-slate-500">
                    {doc.email} &middot; /dr/{doc.slug}
                    {doc.work_pincode && <span className="ml-1 font-mono text-[10px] bg-sky-50 text-sky-600 px-1 rounded">PIN {doc.work_pincode}</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {doc.locations_missing_pincode > 0 && (
                  <span className="text-[9px] text-amber-600 font-medium">{doc.locations_missing_pincode} loc missing pin</span>
                )}
                {doc.tier === 'premium' ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 text-[10px] font-bold border border-amber-200"><Crown size={10} /> PREMIUM</span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold border border-slate-200"><Shield size={10} /> FREE</span>
                )}
                {expanded === doc.id ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
              </div>
            </div>

            {expanded === doc.id && (
              <div className="px-5 pb-5 pt-0 border-t border-slate-100">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-xs">
                  <div><span className="text-slate-500">Specialties:</span> <span className="font-medium">{(doc.specializations || []).join(', ') || '-'}</span></div>
                  <div><span className="text-slate-500">Experience:</span> <span className="font-medium">{doc.experience_years || 0} yrs</span></div>
                  <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{doc.phone || '-'}</span></div>
                  <div><span className="text-slate-500">Work PIN:</span> <span className="font-mono font-medium">{doc.work_pincode || '-'}</span></div>
                </div>
                {/* Locations */}
                {doc.locations && doc.locations.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Locations ({doc.locations.length})</p>
                    <div className="grid md:grid-cols-2 gap-2">
                      {doc.locations.map(loc => (
                        <div key={loc.id} className="bg-slate-50 rounded-lg p-2.5 text-xs">
                          <p className="font-medium text-slate-800">{loc.name} <span className="text-slate-400 font-normal">({loc.type})</span></p>
                          <p className="text-slate-500">{loc.city}{loc.state ? `, ${loc.state}` : ''}{loc.pincode ? ` — ${loc.pincode}` : ''}</p>
                          {!loc.pincode && <p className="text-amber-500 text-[10px] font-medium mt-0.5">Missing pincode</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {doc.active_subscription && (
                  <div className="mt-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs">
                    <p className="font-medium text-emerald-800">Active: {doc.active_subscription.plan_name}</p>
                    <p className="text-emerald-600">Ends: {doc.active_subscription.end_date?.split('T')[0] || '-'}</p>
                  </div>
                )}
                <div className="mt-4 flex items-center gap-2">
                  <span className="text-xs text-slate-500">Set Tier:</span>
                  <button onClick={() => handleSetTier(doc.id, 'free')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${doc.tier === 'free' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    data-testid={`set-free-${doc.slug}`}>Free</button>
                  <button onClick={() => handleSetTier(doc.id, 'premium')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${doc.tier === 'premium' ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
                    data-testid={`set-premium-${doc.slug}`}>Premium</button>
                  <a href={`/dr/${doc.slug}`} target="_blank" rel="noopener noreferrer"
                    className="ml-auto flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700 font-medium"
                    data-testid={`view-site-${doc.slug}`}><ExternalLink size={12} /> View Site</a>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function SubscriptionsTab() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/subscriptions').then(r => setSubs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin text-slate-600 mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-subscriptions">
      <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Subscription History</h3>
      {subs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
          <CreditCard size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600">No subscriptions yet</p>
          <p className="text-xs text-slate-500 mt-1">Subscriptions will appear here when doctors upgrade to Premium.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left">
                <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Doctor</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Plan</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Amount</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Method</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody>
              {subs.map(sub => (
                <tr key={sub.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`sub-${sub.id}`}>
                  <td className="px-5 py-3 text-slate-900 font-medium">{sub.doctor_name}</td>
                  <td className="px-5 py-3 text-slate-600">{sub.plan_name}</td>
                  <td className="px-5 py-3 text-slate-600">Rs. {(sub.amount / 100).toFixed(0)}</td>
                  <td className="px-5 py-3 text-slate-600">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                      sub.payment_method === 'upi_manual' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'
                    }`}>
                      {sub.payment_method === 'upi_manual' ? 'UPI' : 'Razorpay'}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                      sub.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                      sub.status === 'expired' ? 'bg-slate-100 text-slate-500' :
                      sub.status === 'created' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>{sub.status.toUpperCase()}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{sub.created_at?.split('T')[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ========== COUNTRY ADMINS TAB (Super Admin Only) ==========
function CountryAdminsTab() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', country: '', phone: '' });
  const [formError, setFormError] = useState('');
  const [showAdminPw, setShowAdminPw] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/country-admins').then(r => setAdmins(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password || !form.country) {
      setFormError('Name, email, password, and country are required');
      return;
    }
    setCreating(true);
    setFormError('');
    try {
      await api.post('/admin/country-admins', form);
      setShowForm(false);
      setForm({ name: '', email: '', password: '', country: '', phone: '' });
      load();
    } catch (e) {
      setFormError(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove country admin "${name}"? This will delete their account.`)) return;
    try {
      await api.delete(`/admin/country-admins/${id}`);
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin text-slate-600 mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-country-admins">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Country Admins</h3>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-sky-600 text-white text-sm font-medium hover:bg-sky-700"
          data-testid="add-country-admin-btn">
          <Plus size={14} /> Add Country Admin
        </button>
      </div>

      <p className="text-xs text-slate-500">
        Assign regional admins who can manage doctors and payments in their country. Each admin gets their own /admin login.
      </p>

      {showForm && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="country-admin-form">
          <h4 className="text-sm font-medium text-slate-900">New Country Admin</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Full Name *</label>
              <input type="text" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Rahim Khan" data-testid="ca-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Country *</label>
              <input type="text" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
                value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} placeholder="e.g., Bangladesh" data-testid="ca-country" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Email *</label>
              <input type="email" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="admin@country.com" data-testid="ca-email" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Password *</label>
              <div className="relative">
                <input type={showAdminPw ? 'text' : 'password'} className="w-full rounded-xl border border-slate-200 px-3 py-2 pr-10 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
                  value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Set a password" data-testid="ca-password" />
                <button type="button" onClick={() => setShowAdminPw(!showAdminPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" tabIndex={-1}>
                  {showAdminPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Phone</label>
              <input type="text" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
                value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+880..." data-testid="ca-phone" />
            </div>
          </div>
          {formError && <p className="text-xs text-red-600">{formError}</p>}
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={creating}
              className="px-5 py-2 rounded-full bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
              data-testid="ca-submit">
              {creating ? 'Creating...' : 'Create Admin'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-5 py-2 rounded-full bg-slate-100 text-slate-600 text-sm font-medium hover:bg-slate-200">
              Cancel
            </button>
          </div>
        </div>
      )}

      {admins.length === 0 && !showForm ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
          <Globe size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600">No country admins yet</p>
          <p className="text-xs text-slate-500 mt-1">Add regional admins to manage DocSite in different countries.</p>
        </div>
      ) : (
        admins.map(a => (
          <div key={a.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center justify-between" data-testid={`ca-${a.id}`}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-700 font-bold text-sm">
                {a.country?.slice(0, 2).toUpperCase() || '??'}
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">{a.name}</p>
                <p className="text-xs text-slate-500">{a.email} &middot; {a.country}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">{a.status?.toUpperCase()}</span>
              <button onClick={() => handleDelete(a.id, a.name)}
                className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100" data-testid={`delete-ca-${a.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}


// ========== SEO COMMAND CENTER TAB ==========

// ========== SEO COMMAND CENTER TAB ==========
function SeoCommandCenterTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const o = await api.get('/admin/seo/overview');
      setData(o.data);
      setSettings(o.data.settings);
    } catch (e) {
      setMsg(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSavingSettings(true); setMsg('');
    try {
      const r = await api.post('/admin/seo/settings', settings);
      setSettings(r.data);
      setMsg('Settings saved.');
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setSavingSettings(false); }
  };

  if (loading) return <div className="text-center py-12 text-slate-400 animate-pulse">Loading SEO health…</div>;
  if (!data) return <div className="text-center py-12 text-slate-500">Unable to load.</div>;

  const t = data.totals;

  return (
    <div className="space-y-8" data-testid="seo-command-center">
      <div>
        <h2 className="text-xl font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>SEO Command Center</h2>
        <p className="text-sm text-slate-500">Monitor DocSite's AI content engine — each doctor owns their content; DocSite is a per-doctor SEO machine, not a directory.</p>
      </div>

      {msg && <div className="text-sm bg-sky-50 text-sky-700 border border-sky-200 rounded-xl px-4 py-3" data-testid="seo-admin-msg">{msg}</div>}

      {/* HEALTH OVERVIEW */}
      <div>
        <h3 className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">Health Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Active Doctors', value: t.active_doctors, icon: Users },
            { label: 'Articles Published', value: t.articles_published, icon: CheckCircle },
            { label: 'Articles Pending', value: t.articles_pending, icon: Clock, accent: t.articles_pending > 0 ? 'text-amber-600' : 'text-slate-600' },
            { label: 'Procedure Pages', value: t.procedure_pages, icon: Sparkles },
            { label: 'Articles This Month', value: t.articles_this_month, icon: Newspaper },
            { label: 'Articles Last 7 Days', value: t.articles_last_7d, icon: TrendingUp },
            { label: 'Avg Approval Time', value: t.avg_approval_hours == null ? '—' : `${t.avg_approval_hours}h`, icon: Clock },
            { label: 'Total Page Views', value: t.total_page_views, icon: Eye },
          ].map(({ label, value, icon: Icon, accent }) => (
            <div key={label} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`seo-stat-${label.toLowerCase().replace(/ /g, '-')}`}>
              <Icon size={14} className="text-slate-400 mb-1.5" />
              <p className={`text-2xl font-semibold ${accent || 'text-slate-900'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* PENDING APPROVALS BY DOCTOR */}
      {data.pending_by_doctor.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-base font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Clock size={16} className="inline mr-2 text-amber-500" />Doctors with Pending Articles
          </h3>
          <div className="space-y-2">
            {data.pending_by_doctor.map((d) => (
              <div key={d.doctor_id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-200" data-testid={`pending-${d.doctor_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{d.doctor_name}</p>
                  <p className="text-xs text-slate-500">/{d.doctor_slug}</p>
                </div>
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">{d.pending_count} pending</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SETTINGS */}
      {settings && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-base font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Settings size={16} className="inline mr-2 text-slate-600" />AI Engine Settings
          </h3>

          <label className="flex items-start gap-3 cursor-pointer mb-5" data-testid="auto-publish-toggle">
            <input
              type="checkbox"
              checked={!!settings.auto_publish_articles}
              onChange={(e) => setSettings({ ...settings, auto_publish_articles: e.target.checked })}
              className="w-5 h-5 mt-0.5 accent-sky-600"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-900">Auto-publish cron-generated articles</p>
              <p className="text-xs text-slate-500 mt-0.5">
                When ON, monthly AI articles skip doctor review and are published immediately. Doctor-triggered manual generations always require approval.
              </p>
            </div>
          </label>

          <div className="mb-5">
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-1.5 block">
              Article system-prompt override <span className="text-slate-400 font-normal normal-case">— blank = use default</span>
            </label>
            <textarea
              value={settings.article_prompt_override || ''}
              onChange={(e) => setSettings({ ...settings, article_prompt_override: e.target.value })}
              rows={5}
              maxLength={4000}
              placeholder="Leave empty to use DocSite's default medical SEO prompt…"
              className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm font-mono focus:outline-none focus:border-sky-500"
              data-testid="article-prompt-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">{(settings.article_prompt_override || '').length} / 4000 chars</p>
          </div>

          <div className="mb-5">
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-1.5 block">
              Procedure-page system-prompt override <span className="text-slate-400 font-normal normal-case">— applies to /dr/{'{slug}'}/procedures/{'{slug}'} pages</span>
            </label>
            <textarea
              value={settings.procedure_prompt_override || ''}
              onChange={(e) => setSettings({ ...settings, procedure_prompt_override: e.target.value })}
              rows={4}
              maxLength={4000}
              placeholder="Leave empty to use DocSite's default per-doctor procedure page prompt…"
              className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm font-mono focus:outline-none focus:border-sky-500"
              data-testid="procedure-prompt-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">{(settings.procedure_prompt_override || '').length} / 4000 chars</p>
          </div>

          <button
            onClick={saveSettings}
            disabled={savingSettings}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-slate-900 text-white rounded-xl hover:bg-slate-800 disabled:opacity-50"
            data-testid="save-seo-settings-btn"
          >
            {savingSettings ? <Loader2 size={14} className="animate-spin" /> : <Settings size={14} />}Save settings
          </button>
          {settings.updated_at && <p className="text-[11px] text-slate-400 mt-2">Last updated {new Date(settings.updated_at).toLocaleString()} by {settings.updated_by || 'admin'}</p>}
        </div>
      )}

      {/* RECENT ARTICLES */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-base font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Newspaper size={16} className="inline mr-2 text-sky-600" />Recent Article Generations
        </h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {data.recent_articles.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{a.title}</p>
                <p className="text-[11px] text-slate-500 truncate">{a.doctor_name} · {new Date(a.generated_at).toLocaleDateString()}</p>
              </div>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                a.status === 'published' ? 'bg-emerald-100 text-emerald-700' :
                a.status === 'rejected' ? 'bg-slate-100 text-slate-500' :
                'bg-amber-100 text-amber-700'
              }`}>{a.status.replace('_', ' ')}</span>
            </div>
          ))}
        </div>
      </div>

      {/* HealthifyX feed note */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-xs text-slate-500" data-testid="healthifyx-note">
        <p className="font-medium text-slate-700 mb-1">HealthifyX data feed</p>
        <p>Multi-doctor search, pincode queries, and directory aggregation are exposed to the HealthifyX product via the internal API at <code className="bg-white px-1 py-0.5 rounded">/api/internal/directory/*</code> (shared-key auth). Those surfaces are intentionally not visible on DocSite.</p>
      </div>
    </div>
  );
}



// ========== FEEDBACK TAB ==========
const FEEDBACK_STATUSES = [
  { key: 'new', label: 'New', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  { key: 'in_review', label: 'In Review', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'resolved', label: 'Resolved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'closed', label: 'Closed', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
];
const FEEDBACK_CATEGORIES = {
  bug: 'Bug', content: 'Content', billing: 'Billing', feature: 'Feature Request', other: 'Other',
};

function FeedbackTab({ onCountChange }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const q = statusFilter ? `?status=${statusFilter}` : '';
    api.get(`/admin/feedback${q}`)
      .then(r => {
        setItems(r.data.items || []);
        setCounts(r.data.counts || {});
        if (typeof onCountChange === 'function') onCountChange(r.data.counts?.new || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [statusFilter, onCountChange]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/feedback/${id}`, { status });
    load();
  };

  const saveNotes = async (id, admin_notes) => {
    await api.patch(`/admin/feedback/${id}`, { admin_notes });
    load();
  };

  return (
    <div className="space-y-5" data-testid="admin-feedback-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Platform Feedback</h3>
          <p className="text-xs text-slate-500">Messages from doctors, patients, and visitors via the Contact form.</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50" data-testid="feedback-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {FEEDBACK_STATUSES.map(s => (
          <button key={s.key} onClick={() => setStatusFilter(statusFilter === s.key ? '' : s.key)}
            className={`rounded-xl border p-3 text-left transition-all ${s.cls} ${statusFilter === s.key ? 'ring-2 ring-offset-1 ring-slate-400' : ''}`}
            data-testid={`feedback-counter-${s.key}`}>
            <p className="text-[10px] uppercase tracking-wider font-bold opacity-70">{s.label}</p>
            <p className="text-xl font-semibold mt-0.5">{counts[s.key] ?? 0}</p>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="animate-spin text-sky-600 mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-500" data-testid="feedback-empty">
          No feedback {statusFilter ? `with status "${statusFilter}"` : 'yet'}. When users submit via the Contact form, it'll land here.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((fb) => {
            const statusMeta = FEEDBACK_STATUSES.find(s => s.key === fb.status) || FEEDBACK_STATUSES[0];
            const expanded = expandedId === fb.id;
            return (
              <div key={fb.id} className="bg-white rounded-2xl border border-slate-200 p-4 md:p-5" data-testid={`feedback-item-${fb.id}`}>
                <div className="flex items-start justify-between gap-4 cursor-pointer" onClick={() => setExpandedId(expanded ? null : fb.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusMeta.cls}`}>{statusMeta.label}</span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-medium flex items-center gap-1"><Tag size={9} />{FEEDBACK_CATEGORIES[fb.category] || fb.category}</span>
                      <span className="text-[10px] text-slate-500">{new Date(fb.created_at).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-slate-900">{fb.name}</span>
                      <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 text-[10px] font-medium capitalize">{fb.role}</span>
                      {fb.email && <span className="text-xs text-slate-500 flex items-center gap-1"><Mail size={10} />{fb.email}</span>}
                    </div>
                    <p className={`text-sm text-slate-600 mt-1.5 ${expanded ? '' : 'line-clamp-2'}`}>{fb.message}</p>
                  </div>
                  <ChevronDown size={16} className={`text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </div>

                {expanded && (
                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-3" data-testid={`feedback-detail-${fb.id}`}>
                    {fb.phone && <p className="text-xs text-slate-600"><b>Phone:</b> {fb.phone}</p>}
                    {fb.page_url && <p className="text-xs text-slate-600 break-all"><b>Page:</b> {fb.page_url}</p>}

                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Admin Notes (private)</p>
                      <NotesEditor initial={fb.admin_notes || ''} onSave={(v) => saveNotes(fb.id, v)} testid={`feedback-notes-${fb.id}`} />
                    </div>

                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mr-1">Set status:</span>
                      {FEEDBACK_STATUSES.filter(s => s.key !== fb.status).map(s => (
                        <button key={s.key} onClick={() => updateStatus(fb.id, s.key)}
                          className={`px-3 py-1 rounded-full text-[11px] font-medium border hover:shadow-sm ${s.cls}`}
                          data-testid={`feedback-set-${s.key}-${fb.id}`}>
                          → {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NotesEditor({ initial, onSave, testid }) {
  const [val, setVal] = useState(initial);
  const [saving, setSaving] = useState(false);
  const dirty = val !== initial;
  const handleSave = async () => {
    setSaving(true);
    try { await onSave(val); } finally { setSaving(false); }
  };
  return (
    <div>
      <textarea value={val} onChange={(e) => setVal(e.target.value)} rows={3} maxLength={4000}
        placeholder="Internal notes, resolution summary, who's handling this..."
        className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none text-xs resize-none"
        data-testid={testid} />
      {dirty && (
        <button onClick={handleSave} disabled={saving}
          className="mt-1.5 text-xs font-medium text-sky-600 hover:text-sky-700 disabled:opacity-50"
          data-testid={`${testid}-save`}>
          {saving ? 'Saving…' : 'Save notes'}
        </button>
      )}
    </div>
  );
}


// ========== SECURITY TAB ==========
function SecurityTab() {
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const [oldPhrase, setOldPhrase] = useState('');
  const [newPhrase, setNewPhrase] = useState('');
  const [phraseMsg, setPhraseMsg] = useState('');
  const [phraseLoading, setPhraseLoading] = useState(false);
  const [showOldPhrase, setShowOldPhrase] = useState(false);
  const [showNewPhrase, setShowNewPhrase] = useState(false);

  const [encStatus, setEncStatus] = useState(null);

  useEffect(() => {
    api.get('/admin/encryption-status').then(r => setEncStatus(r.data)).catch(() => {});
  }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setPwMsg('Passwords do not match'); return; }
    setPwLoading(true); setPwMsg('');
    try {
      const { data } = await api.post('/auth/change-password', { old_password: oldPw, new_password: newPw });
      setPwMsg(data.message);
      setOldPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) { setPwMsg(formatApiError(err)); }
    setPwLoading(false);
  };

  const changePassphrase = async (e) => {
    e.preventDefault();
    if (!oldPhrase || !newPhrase) { setPhraseMsg('Both fields required'); return; }
    setPhraseLoading(true); setPhraseMsg('');
    try {
      const { data } = await api.post('/admin/change-passphrase', { old_passphrase: oldPhrase, new_passphrase: newPhrase });
      setPhraseMsg(`${data.message}`);
      setOldPhrase(''); setNewPhrase('');
      api.get('/admin/encryption-status').then(r => setEncStatus(r.data)).catch(() => {});
    } catch (err) { setPhraseMsg(formatApiError(err)); }
    setPhraseLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="security-tab">
      {/* Encryption Status */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Shield size={18} className="text-sky-600" /> Encryption Status
        </h3>
        {encStatus ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs text-slate-500">Key Source</p>
              <p className="text-sm font-bold text-slate-900 mt-1">{encStatus.key_source === 'passphrase' ? 'Passphrase (PBKDF2)' : encStatus.key_source}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs text-slate-500">Encrypted Users</p>
              <p className="text-sm font-bold text-emerald-600 mt-1">{encStatus.encrypted_users}/{encStatus.total_users}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs text-slate-500">Status</p>
              <p className={`text-sm font-bold mt-1 ${encStatus.fully_encrypted ? 'text-emerald-600' : 'text-amber-600'}`}>
                {encStatus.fully_encrypted ? 'Fully Encrypted' : 'Partial'}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs text-slate-500">Algorithm</p>
              <p className="text-sm font-bold text-slate-900 mt-1">AES-128-CBC (Fernet)</p>
            </div>
          </div>
        ) : <Loader2 size={16} className="animate-spin text-slate-400" />}
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <KeyRound size={18} className="text-sky-600" /> Change Admin Password
        </h3>
        <form onSubmit={changePassword} className="space-y-4 max-w-md">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Current Password</label>
            <div className="relative">
              <input type={showOldPw ? 'text' : 'password'} value={oldPw} onChange={e => setOldPw(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
                data-testid="sec-old-password" />
              <button type="button" onClick={() => setShowOldPw(!showOldPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                {showOldPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">New Password</label>
            <div className="relative">
              <input type={showNewPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
                placeholder="Min 8 chars, 1 uppercase, 1 number" data-testid="sec-new-password" />
              <button type="button" onClick={() => setShowNewPw(!showNewPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                {showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Confirm New Password</label>
            <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
              data-testid="sec-confirm-password" />
          </div>
          {pwMsg && <p className={`text-xs ${pwMsg.includes('success') ? 'text-emerald-600' : 'text-red-600'}`}>{pwMsg}</p>}
          <button type="submit" disabled={pwLoading}
            className="px-6 py-2.5 rounded-xl bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2"
            data-testid="sec-change-password-btn">
            {pwLoading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} Change Password
          </button>
        </form>
      </div>

      {/* Change Encryption Passphrase */}
      <div className="bg-white rounded-2xl border border-amber-200 p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-2 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <AlertTriangle size={18} className="text-amber-600" /> Change Encryption Passphrase
        </h3>
        <p className="text-xs text-slate-500 mb-4 leading-relaxed">
          This will re-encrypt ALL sensitive data with a new passphrase. After changing, you MUST update
          the <code className="bg-slate-100 px-1 rounded font-bold">FOUNDER_PASSPHRASE</code> in your Emergent deployment settings to the new passphrase.
          If you redeploy without updating it, the app will not be able to decrypt your data.
        </p>
        <form onSubmit={changePassphrase} className="space-y-4 max-w-md">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Current Passphrase</label>
            <div className="relative">
              <input type={showOldPhrase ? 'text' : 'password'} value={oldPhrase} onChange={e => setOldPhrase(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-amber-500/20"
                data-testid="sec-old-passphrase" />
              <button type="button" onClick={() => setShowOldPhrase(!showOldPhrase)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                {showOldPhrase ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">New Passphrase (min 10 characters)</label>
            <div className="relative">
              <input type={showNewPhrase ? 'text' : 'password'} value={newPhrase} onChange={e => setNewPhrase(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-amber-500/20"
                placeholder="A memorable phrase only you know" data-testid="sec-new-passphrase" />
              <button type="button" onClick={() => setShowNewPhrase(!showNewPhrase)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                {showNewPhrase ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          {phraseMsg && <p className={`text-xs ${phraseMsg.includes('success') ? 'text-emerald-600' : 'text-red-600'}`}>{phraseMsg}</p>}
          <button type="submit" disabled={phraseLoading}
            className="px-6 py-2.5 rounded-xl bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
            data-testid="sec-change-passphrase-btn">
            {phraseLoading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Re-encrypt All Data
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Data Fixes Tab — super-admin data repair operations
// ============================================================================
function DataFixesTab() {
  // Phone fix form state
  const [email, setEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [phoneResult, setPhoneResult] = useState(null);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  // Backfill UUIDs state
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillError, setBackfillError] = useState('');

  const handlePhoneFix = async (e) => {
    e.preventDefault();
    if (!email.trim() || !newPhone.trim()) {
      setPhoneError('Both email and phone are required');
      return;
    }
    if (!window.confirm(`Update phone for ${email.trim()} to ${newPhone.trim()}? This cannot be undone easily.`)) return;
    setPhoneLoading(true); setPhoneError(''); setPhoneResult(null);
    try {
      const { data } = await api.post('/admin/data-fix/user-phone', {
        email: email.trim().toLowerCase(),
        new_phone: newPhone.trim(),
      });
      setPhoneResult(data);
    } catch (err) {
      setPhoneError(formatApiError(err.response?.data?.detail) || 'Failed');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleBackfill = async () => {
    if (!window.confirm('Scan all user records and add UUID id fields where missing. This is safe and idempotent. Continue?')) return;
    setBackfillLoading(true); setBackfillError(''); setBackfillResult(null);
    try {
      const { data } = await api.post('/admin/data-fix/backfill-user-ids');
      setBackfillResult(data);
    } catch (err) {
      setBackfillError(formatApiError(err.response?.data?.detail) || 'Failed');
    } finally {
      setBackfillLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="data-fixes-warning">
        <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-semibold">Super-admin only. Every action is audit-logged with your admin email.</p>
          <p className="text-xs mt-1 text-amber-700">Use these only for legitimate data-repair needs (e.g., fixing incorrect phone from seed data).</p>
        </div>
      </div>

      {/* Fix User Phone */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="fix-phone-card">
        <div className="flex items-center gap-2 mb-1">
          <UserCog size={18} className="text-sky-600" />
          <h3 className="text-base font-semibold text-slate-900">Update User Phone Number</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">Re-encrypts + re-hashes the user's phone. Also updates the linked doctor record (if any).</p>
        <form onSubmit={handlePhoneFix} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">User Email</label>
            <input type="email" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-sky-400"
              value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com"
              data-testid="fix-phone-email-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">New Phone Number</label>
            <input type="tel" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-sky-400"
              value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="9876543210 (with or without +91)"
              data-testid="fix-phone-number-input" />
            <p className="text-[11px] text-slate-400 mt-1">Accepts 9876543210, +91 9876543210, 919876543210 — normalizes to 10 digits.</p>
          </div>
          <button type="submit" disabled={phoneLoading}
            className="px-5 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2"
            data-testid="fix-phone-submit-btn">
            {phoneLoading ? <Loader2 size={14} className="animate-spin" /> : <Wrench size={14} />}
            {phoneLoading ? 'Updating…' : 'Update Phone'}
          </button>
          {phoneError && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700" data-testid="fix-phone-error">{phoneError}</div>
          )}
          {phoneResult && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800" data-testid="fix-phone-success">
              <p className="font-medium">✓ Phone updated successfully</p>
              <ul className="mt-1 space-y-0.5 text-emerald-700">
                <li>• Old phone: {phoneResult.old_phone_masked || 'none'}</li>
                <li>• New phone: {phoneResult.new_phone_masked}</li>
                <li>• Doctor record updated: {phoneResult.doctors_updated ? 'yes' : 'no matching record'}</li>
                {phoneResult.uuid_backfilled && <li>• Also backfilled UUID id on this user</li>}
              </ul>
            </div>
          )}
        </form>
      </div>

      {/* Backfill UUIDs */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="backfill-uuids-card">
        <div className="flex items-center gap-2 mb-1">
          <DatabaseZap size={18} className="text-sky-600" />
          <h3 className="text-base font-semibold text-slate-900">Backfill User UUIDs</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">Scans all user records for missing UUID <code className="bg-slate-100 px-1 rounded">id</code> fields (needed for password-reset and other lookups). Safe and idempotent.</p>
        <button onClick={handleBackfill} disabled={backfillLoading}
          className="px-5 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
          data-testid="backfill-uuids-btn">
          {backfillLoading ? <Loader2 size={14} className="animate-spin" /> : <DatabaseZap size={14} />}
          {backfillLoading ? 'Scanning…' : 'Run Backfill'}
        </button>
        {backfillError && (
          <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700" data-testid="backfill-error">{backfillError}</div>
        )}
        {backfillResult && (
          <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800" data-testid="backfill-success">
            ✓ Backfilled <strong>{backfillResult.backfilled}</strong> user record{backfillResult.backfilled !== 1 ? 's' : ''} with new UUID id fields.
          </div>
        )}
      </div>
    </div>
  );
}



// ============================================================================
// Coupons Tab — privileged access code management + redemption audit trail
// ============================================================================
function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [code, setCode] = useState('');
  const [tier, setTier] = useState('premium');
  const [months, setMonths] = useState(12);
  const [maxUses, setMaxUses] = useState(1);
  const [restrictEmail, setRestrictEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [c, r] = await Promise.all([
        api.get('/admin/coupons'),
        api.get('/admin/coupons/redemptions?limit=200'),
      ]);
      setCoupons(c.data.rows || []);
      setRedemptions(r.data.rows || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setCreating(true); setCreateMsg('');
    try {
      await api.post('/admin/coupons', {
        code: code.trim().toUpperCase(),
        tier,
        months_granted: Number(months) || 12,
        max_uses: Number(maxUses) || 1,
        restricted_to_email: restrictEmail.trim() || null,
        notes: notes.trim() || null,
      });
      setCreateMsg(`Created: ${code.trim().toUpperCase()}`);
      setCode(''); setRestrictEmail(''); setNotes('');
      load();
      setTimeout(() => setCreateMsg(''), 4000);
    } catch (e2) {
      setCreateMsg('Error: ' + (formatApiError(e2.response?.data?.detail) || 'Failed'));
    } finally { setCreating(false); }
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin text-sky-600 mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="coupons-tab">
      {err && <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700">{err}</div>}

      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="create-coupon-card">
        <div className="flex items-center gap-2 mb-1">
          <Gift size={18} className="text-amber-600" />
          <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Create Privileged Access Coupon</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">Grants a DocSite doctor a paid tier (premium / turbo) for N months — free — when they enter the code in their dashboard. Every redemption is audit-logged below.</p>
        <form onSubmit={handleCreate} className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Code</label>
            <input value={code} onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. FOUNDER-GIFT-2026" required minLength={4} maxLength={40}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono tracking-wider outline-none focus:border-amber-400"
              data-testid="create-coupon-code-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Tier</label>
            <select value={tier} onChange={e => setTier(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400"
              data-testid="create-coupon-tier-select">
              <option value="premium">Premium</option>
              <option value="turbo">Turbo</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Months Granted</label>
            <input type="number" min={1} max={60} value={months} onChange={e => setMonths(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400"
              data-testid="create-coupon-months-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Max Uses</label>
            <input type="number" min={1} max={10000} value={maxUses} onChange={e => setMaxUses(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400"
              data-testid="create-coupon-max-uses-input" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-slate-600 mb-1 block">Restrict to Email (optional)</label>
            <input type="email" value={restrictEmail} onChange={e => setRestrictEmail(e.target.value)}
              placeholder="e.g. chetnatikoo@gmail.com"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400"
              data-testid="create-coupon-email-input" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-slate-600 mb-1 block">Notes (optional, internal only)</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Why you are issuing this code"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400"
              data-testid="create-coupon-notes-input" />
          </div>
          <div className="md:col-span-2 flex items-center gap-3">
            <button type="submit" disabled={creating}
              className="px-5 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="create-coupon-submit-btn">
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Create Coupon
            </button>
            {createMsg && <span className={`text-xs font-medium ${createMsg.startsWith('Error') ? 'text-rose-600' : 'text-emerald-600'}`}>{createMsg}</span>}
          </div>
        </form>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="coupons-list-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Ticket size={18} className="text-sky-600" />
            <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>All Coupons ({coupons.length})</h3>
          </div>
          <button onClick={load} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><RefreshCw size={12} /> Refresh</button>
        </div>
        {coupons.length === 0 ? (
          <p className="text-xs text-slate-500">No coupons created yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-2">Code</th>
                  <th className="py-2 px-2">Tier</th>
                  <th className="py-2 px-2">Months</th>
                  <th className="py-2 px-2">Uses</th>
                  <th className="py-2 px-2">Restricted To</th>
                  <th className="py-2 px-2">Created</th>
                  <th className="py-2 px-2">Last Used</th>
                  <th className="py-2 px-2">Notes</th>
                </tr>
              </thead>
              <tbody data-testid="coupons-list-tbody">
                {coupons.map(c => (
                  <tr key={c.code} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-2 font-mono tracking-wider text-slate-900">{c.code}</td>
                    <td className="py-2 px-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${c.tier === 'turbo' ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-amber-100 text-amber-700'}`}>
                        {c.tier}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-slate-600">{c.months_granted}</td>
                    <td className="py-2 px-2 text-slate-600">{c.used_count || 0}/{c.max_uses}</td>
                    <td className="py-2 px-2 text-slate-600 truncate max-w-[180px]">{c.restricted_to_email || '—'}</td>
                    <td className="py-2 px-2 text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                    <td className="py-2 px-2 text-slate-500">{c.last_used_at ? new Date(c.last_used_at).toLocaleDateString() : '—'}</td>
                    <td className="py-2 px-2 text-slate-500 truncate max-w-[200px]">{c.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="coupons-audit-card">
        <div className="flex items-center gap-2 mb-3">
          <Shield size={18} className="text-emerald-600" />
          <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Redemption Audit Trail ({redemptions.length})</h3>
        </div>
        {redemptions.length === 0 ? (
          <p className="text-xs text-slate-500">No coupons have been redeemed yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-2">When</th>
                  <th className="py-2 px-2">Code</th>
                  <th className="py-2 px-2">Doctor</th>
                  <th className="py-2 px-2">Email</th>
                  <th className="py-2 px-2">Tier Change</th>
                  <th className="py-2 px-2">Months</th>
                  <th className="py-2 px-2">New Expiry</th>
                  <th className="py-2 px-2">IP (hashed)</th>
                </tr>
              </thead>
              <tbody data-testid="coupons-audit-tbody">
                {redemptions.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{r.ts ? new Date(r.ts).toLocaleString() : '—'}</td>
                    <td className="py-2 px-2 font-mono tracking-wider text-slate-900">{r.code}</td>
                    <td className="py-2 px-2 text-slate-700">{r.doctor_name || '—'}</td>
                    <td className="py-2 px-2 text-slate-600">{r.user_email || '—'}</td>
                    <td className="py-2 px-2 text-slate-600">
                      <span className="text-slate-400">{r.previous_tier || 'free'}</span>
                      <span className="mx-1">→</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${r.new_tier === 'turbo' ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-amber-100 text-amber-700'}`}>{r.new_tier}</span>
                    </td>
                    <td className="py-2 px-2 text-slate-600">{r.months_granted}</td>
                    <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{r.new_expiry ? new Date(r.new_expiry).toLocaleDateString() : '—'}</td>
                    <td className="py-2 px-2 text-slate-400 font-mono text-[10px]">{(r.ip_hash || '').substring(0, 12) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
